import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  OperationStatus,
  PublicationStatus,
  ShiftCategoriesIdsMap,
  ShiftState,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  ShiftTimelineDataState,
  TimelineCreateNewShiftV2,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { DateTimeFormat } from '@wilson/utils';
import { format, startOfMinute } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject } from 'rxjs';
import { createFullActivityObject } from '@wilson/non-domain-specific/activities-helpers/services';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Injectable()
export class ActivityDropZoneHelperService {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  private activitySubject =
    new BehaviorSubject<ActivityWithCategoryAndLocationsAndReportedDetails | null>(
      null,
    );
  private userIdSubject = new BehaviorSubject<string | null>(null);
  public activity$ = this.activitySubject.asObservable();
  public userId$ = this.userIdSubject.asObservable();

  public setActivity(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails | null,
  ) {
    this.activitySubject.next(activity);
  }

  public setUserId(userId: string | null) {
    this.userIdSubject.next(userId);
  }

  public createShiftWithActivity(userId: string):
    | ({
        activities: ActivityWithCategoryAndLocationsAndReportedDetails[];
      } & Omit<TimelineShiftWithActivities, 'activities'>)
    | null {
    const activity = this.activitySubject.value;
    const organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );
    if (activity?.id && organizationalUnit?.id) {
      const mockActivity = {
        ...createFullActivityObject(),
        activityReports: [],
        id: activity.id,
        activityCategory: activity.activityCategory,
        activityCategoryId: activity.activityCategoryId,
        createdFrom: activity.createdFrom,
        endDatetime: startOfMinute(
          new Date(activity.endDatetime),
        ).toISOString(),
        endLocationId: activity.endLocationId,
        endLocation: activity.endLocation,
        jobId: activity.jobId || null,
        name: activity.name,
        operationalStatus: activity.operationalStatus,
        professionId: activity.professionId || null,
        serviceId: activity.serviceId || null,
        shiftId: activity.shiftId || null,
        startDatetime: startOfMinute(
          new Date(activity.startDatetime),
        ).toISOString(),
        startLocationId: activity.startLocationId,
        startLocation: activity.startLocation,
        viaLocationIds: activity.viaLocationIds,
        agreementId: activity.agreementId || null,
        reportedStartDatetime: null,
        reportedEndDatetime: null,
        reportedStartLocationId: null,
        reportedEndLocationId: null,
      };

      return {
        id: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        comment: '',
        planningComment: null,
        name: `${activity.name} - ${this.translate.instant('page.shift')}`,
        organizationalUnitId: organizationalUnit?.id,
        publicationStatus: PublicationStatus.NotPublished,
        shiftCategoryId: ShiftCategoriesIdsMap.Normal,
        startDate: format(
          new Date(activity.startDatetime),
          DateTimeFormat.DatabaseDateFormat,
        ),
        state: ShiftState.NotSubmitted,
        userId: userId,
        priority: null,
        activities: [mockActivity],
        startDatetime: mockActivity.startDatetime,
        endDatetime: mockActivity.endDatetime,
        deviatedStartDatetime: null,
        deviatedEndDatetime: null,
        reportedStartDatetime: null,
        reportedEndDatetime: null,
        startLocationId: mockActivity.startLocationId as string,
        endLocationId: mockActivity.endLocationId as string,
        startLocation: {
          locationCode: mockActivity.startLocation?.locationCode || '',
          name: mockActivity.startLocation?.name || '',
        },
        endLocation: {
          locationCode: mockActivity.endLocation?.locationCode || '',
          name: mockActivity.endLocation?.name || '',
        },
        reportedStartLocationId: null,
        reportedEndLocationId: null,
        reportedStartLocation: null,
        reportedEndLocation: null,
        operationalStatus: OperationStatus.NotStarted,
        projectId: null,
        ignoreStayNecessary: false,
        sharePlanNumber: null,
        maintenanceNumber: null,
        isHighlighted: true,
      };
    } else {
      return null;
    }
  }

  public async handleAssignActivityToExistingShift(
    activity: Activity | null,
    previousShiftId: string,
  ) {
    if (activity?.id) {
      const previousShift = this.store.selectSnapshot(
        ShiftTimelineDataState.getShiftWithoutActivities(previousShiftId),
      );
      if (previousShift?.userId) {
        const shiftActivities = this.store.selectSnapshot(
          ShiftTimelineDataState.shiftActivitiesArray(previousShiftId),
        );
        if (shiftActivities.length === 1) {
          try {
            this.store.dispatch(
              new TimelineRemoveShiftFromUserV2({
                shiftId: previousShiftId,
                userId: previousShift.userId,
              }),
            );
            await this.shiftsService.deleteShift(previousShiftId);
          } catch {
            this.store.dispatch(
              new TimelineCreateNewShiftV2(previousShift, previousShift.userId),
            );
            this.nzMessageService.error(
              this.translate.instant('general.something_went_wrong'),
            );
          }
        } else {
          this.store.dispatch(
            new TimelineRemoveActivityFromShiftV2(activity.id, previousShiftId),
          );

          this.timelineCommonOperationsService.updateUsersAssignmentsState([
            previousShift.userId,
          ]);
        }
      }
      this.store.dispatch(new SetDemarcatorRange([]));
      this.dragAndDropModeService.setActivityDropZone(false);
      this.dragAndDropModeService.setDraggingItemId(null);
    }
  }
}
